
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/ui/DataTable/index";
import { Combobox } from "@/components/ui/combobox";
import { Button } from "@/components/ui/button";
import { formatNumberToDisplay } from "@/lib/api/common";
import { 
  useListMarketZoneBreakdowns,
  MarketZoneBreakdown,
} from "@/lib/api/risk/marketZoneBreakthrough";
import { useListMarketZones } from "@/lib/api/generalContract";
import { Commodities, CommodityOptions } from "@/lib/api/commodity";
import { CustomerBreakdownView } from "../MarketZoneBreakthroughs/CustomerBreakdownView";
import { type DataTableColumn } from "@/components/ui/DataTable/index";

// Extended interface for data that might include relations
interface MarketZoneBreakdownWithRelations extends MarketZoneBreakdown {
  Customer?: { id: string; long_name: string };
  MarketZone?: { id: string; dtn_market_zone: string };
}

interface MarketZoneSummary {
  market_zone_id: string;
  market_zone_name: string;
  commodity: string;
  crop_year: number;
  total_amount: number;
  total_monthly: {
    month_one: number;
    month_two: number;
    month_three: number;
    month_four: number;
    month_five: number;
    month_six: number;
    month_seven: number;
    month_eight: number;
    month_nine: number;
    month_ten: number;
    month_eleven: number;
    month_twelve: number;
  };
  customer_count: number;
}

export function RiskMarketZoneDashboard() {
  const [cropYear, setCropYear] = useState<number>(new Date().getFullYear());
  const [selectedCommodity, setSelectedCommodity] = useState<string>(Commodities.Corn);
  const [selectedMarketZone, setSelectedMarketZone] = useState<string>("");

  // Generate crop year options (current year and previous 2 years)
  const cropYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 3 }, (_, i) => ({
      value: (currentYear - i).toString(),
      label: (currentYear - i).toString(),
    }));
  }, []);

  const commodityOptions = useMemo(() => {
    return CommodityOptions.map(option => ({
      value: option.value,
      label: option.label,
    }));
  }, []);

  // Fetch data
  const marketZonesQuery = useListMarketZones();
  const breakdownsQuery = useListMarketZoneBreakdowns({
    market_zone_id: selectedMarketZone || undefined,
    commodity: selectedCommodity || undefined,
    crop_year: cropYear,
  });

  const marketZoneOptions = useMemo(() => {
    if (!marketZonesQuery.data?.data) return [];
    return marketZonesQuery.data.data
      .map((zone: { id: string; dtn_market_zone: string }) => ({
        value: zone.id,
        label: zone.dtn_market_zone,
      }));
  }, [marketZonesQuery.data]);

  // Calculate market zone summary data
  const marketZoneSummaryData = useMemo((): MarketZoneSummary[] => {
    if (!breakdownsQuery.data?.data) return [];

    const marketZoneMap = new Map(
      (marketZonesQuery.data?.data || []).map((zone: { id: string; dtn_market_zone: string }) => [zone.id, zone.dtn_market_zone])
    );

    // Group by market zone and aggregate
    const summaryMap = new Map<string, MarketZoneSummary>();

    breakdownsQuery.data.data.forEach((breakdown: MarketZoneBreakdownWithRelations) => {
      const key = breakdown.market_zone_id;
      
      if (!summaryMap.has(key)) {
        summaryMap.set(key, {
          market_zone_id: breakdown.market_zone_id,
          market_zone_name: (marketZoneMap.get(breakdown.market_zone_id) as string) || breakdown.MarketZone?.dtn_market_zone || "Unknown Market Zone",
          commodity: breakdown.commodity,
          crop_year: breakdown.crop_year,
          total_amount: 0,
          total_monthly: {
            month_one: 0,
            month_two: 0,
            month_three: 0,
            month_four: 0,
            month_five: 0,
            month_six: 0,
            month_seven: 0,
            month_eight: 0,
            month_nine: 0,
            month_ten: 0,
            month_eleven: 0,
            month_twelve: 0,
          },
          customer_count: 0,
        });
      }

      const summary = summaryMap.get(key)!;
      
      // Add amounts
      summary.total_amount += parseFloat(breakdown.amount?.toString() || "0");
      summary.customer_count += 1;
      
      // Add monthly totals
      const monthFields = [
        "month_one", "month_two", "month_three", "month_four",
        "month_five", "month_six", "month_seven", "month_eight",
        "month_nine", "month_ten", "month_eleven", "month_twelve"
      ] as const;
      
      monthFields.forEach((monthKey) => {
        const monthValue = parseFloat(breakdown[monthKey]?.toString() || "0");
        summary.total_monthly[monthKey] += monthValue;
      });
    });

    return Array.from(summaryMap.values());
  }, [breakdownsQuery.data, marketZonesQuery.data]);

  // Create summary table columns
  const summaryColumns = useMemo((): DataTableColumn<MarketZoneSummary>[] => {
    const columns: DataTableColumn<MarketZoneSummary>[] = [];

    // Market Zone column
    columns.push({
      id: "market_zone_name",
      accessorKey: "market_zone_name",
      header: "Market Zone",
      cell: (info) => info.getValue() as string,
      minSize: 120,
      maxSize: 180,
      enableSorting: true,
    });



    // Total Amount column
    columns.push({
      id: "total_amount",
      accessorKey: "total_amount",
      header: "Total Amount",
      cell: (info) => formatNumberToDisplay(info.getValue() as any, {
        passthroughEmpty: false, 
        emptyFormat: "0",
        maximumFractionDigits: 0 
      }),
      minSize: 120,
      maxSize: 150,
      enableSorting: false,
      enableFiltering: false,
    });

    // Monthly columns
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const monthFields = [
      "month_one", "month_two", "month_three", "month_four",
      "month_five", "month_six", "month_seven", "month_eight",
      "month_nine", "month_ten", "month_eleven", "month_twelve"
    ] as const;

    monthFields.forEach((field, index) => {
      columns.push({
        id: field,
        accessorKey: `total_monthly.${field}`,
        header: monthNames[index],
        cell: (info) => formatNumberToDisplay(info.getValue() as any, {
          passthroughEmpty: false, 
          emptyFormat: "0",
          maximumFractionDigits: 0 
        }),
        minSize: 80,
        maxSize: 100,
      enableSorting: false,
      enableFiltering: false,
      });
    });

    return columns;
  }, []);

  const isLoading = breakdownsQuery.isLoading || marketZonesQuery.isLoading;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <h1 className="text-3xl font-bold">Projected Sales Dashboard</h1>
        
        {/* Global Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Combobox
            options={cropYearOptions}
            value={cropYear.toString()}
            onValueChange={(value) => setCropYear(parseInt(value))}
            placeholder="Select crop year"
            dwimOnChange
          />
          <Combobox
            options={commodityOptions}
            value={selectedCommodity}
            onValueChange={setSelectedCommodity}
            placeholder="Select commodity"
            dwimOnChange
          />
          <Combobox
            options={marketZoneOptions}
            value={selectedMarketZone}
            onValueChange={setSelectedMarketZone}
            placeholder="All market zones"
            dwimOnChange
          />
        </div>
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="customer-detail" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="customer-detail">Customer Detail View</TabsTrigger>
          <TabsTrigger value="summary">Market Zone Summary</TabsTrigger>
        </TabsList>
        
        <TabsContent value="summary" className="space-y-4">
              <DataTable
                className="shadow-lg"
                stickyHeaders
                tableClassName="max-h-[calc(100vh-400px)] overflow-y-scroll"
                data={marketZoneSummaryData}
                columns={summaryColumns}
                enableSorting
                enableMultiSort
                enableFiltering
                enableExport
                enableColumnVisibility
                filterStyle="select"
                filterMode="internal"
                emptyMessage={
                  !selectedCommodity
                    ? "Please select a commodity to view summary"
                    : isLoading
                    ? "Loading..."
                    : "No market zone data found"
                }
              />
        </TabsContent>
        
        <TabsContent value="customer-detail" className="space-y-4">
              <CustomerBreakdownView
                crop_year={cropYear}
                useExternalFilters={true}
                externalFilters={{
                  crop_year: cropYear,
                  selectedCommodity: selectedCommodity,
                  selectedMarketZone: selectedMarketZone,
                }}
                onFilterChange={(filters) => {
                  if (filters.crop_year !== undefined) setCropYear(filters.crop_year);
                  if (filters.selectedCommodity !== undefined) setSelectedCommodity(filters.selectedCommodity);
                  if (filters.selectedMarketZone !== undefined) setSelectedMarketZone(filters.selectedMarketZone);
                }}
              />
        </TabsContent>
      </Tabs>
    </div>
  );
}
